// src/components/Form/VehicleManagement.jsx
// UPDATED: Full detailed Vehicle Management component aligned to schema with File Uploads.
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../Context/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  FaCar,
  FaCheckCircle,
  FaTimesCircle,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaSpinner,
  FaCheck,
  FaExclamationTriangle,
  FaBuilding,
  FaUser,
  FaFileUpload,
  FaFileAlt,
  FaEye,
  FaFilter
} from 'react-icons/fa';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';

const VehicleManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [vehicles, setVehicles] = useState([]);

  // Global State
  const { baseUrl, configLoading, user } = useContext(UserContext);

  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [vehiclesPerPage] = useState(5);
  
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [pendingSaveEvent, setPendingSaveEvent] = useState(null);

  const handleFormSubmitClick = (e) => {
    e.preventDefault();
    setPendingSaveEvent(e);
    setShowSaveConfirm(true);
  };

  const confirmAndSave = () => {
    setShowSaveConfirm(false);
    if (selectedVehicle) {
      handleEditVehicle(pendingSaveEvent);
    } else {
      handleAddVehicle(pendingSaveEvent);
    }
  };

  // Filter state for dynamic branches

  // Permissions State
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  
  // Custom State
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [userBranch, setUserBranch] = useState("");
  const [availableBranches, setAvailableBranches] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');
  const [formBranches, setFormBranches] = useState([]);

  const [filterBranches, setFilterBranches] = useState([]);
  const [selectedViewCompany, setSelectedViewCompany] = useState('');

  useEffect(() => {
    const fetchFilterBranches = async () => {
      try {
        const url = baseUrl || "";
        const comp = selectedViewCompany || localStorage.getItem('active_company');
        if (!comp || comp === 'All') {
            setFilterBranches(availableBranches); // fallback
            return;
        }
        const resp = await axios.get(`${url}/api/branches?company_name=${encodeURIComponent(comp)}</div>`);
        setFilterBranches([...new Set(resp.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.name)).filter(Boolean))]);
      } catch (err) {
        console.error("Error fetching filter branches:", err);
      }
    };
    fetchFilterBranches();
  }, [selectedViewCompany, baseUrl, availableBranches]);

  // Auto-open modal based on nav state
  useEffect(() => {
    if (location.state && (location.state.action === 'create' || location.state.openAddModal)) {
      setSelectedVehicle(null);
    }
  }, [location.state]);

  // Initial Data Fetching - ROBUST FIX for Infinite Loading
  useEffect(() => {
    // Wait for config to load
    if (configLoading) return;

    const currentBaseUrl = baseUrl || '';

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([
          fetchVehicles(currentBaseUrl),
          fetchPermissions(currentBaseUrl),
          fetchBranches(currentBaseUrl)
        ]);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchPermissions = async (url) => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userData = JSON.parse(userStr);
          const roleRaw = userData.role || userData.UserType || '';
          const branch = userData.branch || userData.branch_name || "";
          const company = userData.company || userData.company_name || "";
          
          const isAdminRole = checkIsAdmin(userData);
          const isGroupAdminRole = checkIsGlobalAdmin(userData);
          const is_Admin = isAdminRole && (!branch || branch === 'All Branches' || branch === "");
          
          setIsCompanyAdmin(is_Admin);
          setIsGroupAdmin(isGroupAdminRole);
          setUserBranch(userData.branch_name || userData.branch || "");

          if (roleRaw) {
            const headers = {};
            if (company) headers['X-Company-Name'] = company;
            if (branch) headers['X-Branch-Name'] = branch;

            const response = await axios.get(`${url}/api/role-permissions?role=${encodeURIComponent(roleRaw)}`, { headers });
            const perms = response.data.permissions || [];
            const pagePerm = perms.find(p => p.pageId === 'vehicle_management');

            if (isAdminRole || isGroupAdminRole) {
              setCanRead(true);
              setCanWrite(true);
              setCanDelete(true);
              setCanCreate(true);
            } else if (pagePerm) {
              setCanRead(pagePerm.canRead === true);
              setCanWrite(pagePerm.canWrite === true);
              setCanDelete(pagePerm.canDelete === true);
              setCanCreate(pagePerm.canCreate === true);
            }
          }

          const activeContext = localStorage.getItem('active_company');
          fetchCompanies(url);
          if (activeContext && activeContext !== 'All') {
            setSelectedCompanies([activeContext || company]);
          }
        }
      } catch (error) {
        console.warn("Error fetching permissions:", error);
      }
    };

    const fetchCompanies = async (url) => {
      try {
        const userStr = localStorage.getItem('user');
        const userObj = userStr ? JSON.parse(userStr) : {};
        
        let comps = [];
        if (userObj.companies && Array.isArray(userObj.companies) && userObj.companies.length > 0) {
            comps = userObj.companies.map(c => typeof c === 'string' ? c : c.company_name || c.name).filter(Boolean);
        } else if (userObj.company_name) {
            comps = [userObj.company_name];
        }

        if (comps.length > 0) {
            setAllCompanies(comps);
            setCompanyOptions(comps);
            return;
        }

        const resp = await axios.get(`${url}/api/branches`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (Array.isArray(resp.data)) {
          const companies = [...new Set(resp.data.map(b => b.company_name || b.company).filter(Boolean))].filter(c => c !== 'POS 8');
          setAllCompanies(companies);
          setCompanyOptions(companies);
        }
      } catch (err) {
        console.error("Failed to fetch companies:", err);
      }
    };

    const fetchBranches = async (url) => {
      try {
        const userStr = localStorage.getItem('user');
        const headers = {};
        if (userStr) {
          const userData = JSON.parse(userStr);
          if (userData.company_name) headers['X-Company-Name'] = userData.company_name;
        }
        const response = await axios.get(`${url}/api/branches`, { headers });
        const branchData = response.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name));
        setAvailableBranches(branchData);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    fetchData();
  }, [baseUrl, configLoading, selectedViewCompany, selectedBranchFilter]);

  // Form State
  const [formData, setFormData] = useState({
    vehicle_number: '',
    vehicle_type: '',
    brand: '',
    model: '',
    fuel_type: '',
    is_company_owned: true,
    rc_number: '',
    rc_expiry: '',
    insurance_number: '',
    insurance_expiry: '',
    insurance_doc: null, // File object
    pollution_expiry: '',
    pollution_doc: null, // File object
    status: 'ACTIVE',
    branch_names: [],
  });


  // Fetch all vehicles
  const fetchVehicles = async (url) => {
    try {
      const headers = {};
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const branchToUse = selectedBranchFilter || (userData.branch_name || '');
        if (branchToUse && branchToUse !== 'All Branches') headers['X-Branch-Name'] = branchToUse;
        
        const companyToUse = selectedViewCompany || localStorage.getItem('active_company') || (userData.company_name || '');
        if (companyToUse && companyToUse !== 'All') headers['X-Company-Name'] = companyToUse;
      }
      const response = await axios.get(`${url}/api/vehicle/management`, { headers });
      let data = response.data || [];
      
      setVehicles(data);
    } catch (err) {
      console.error('Fetch vehicles error:', err);
      setError(`Failed to fetch vehicles: ${err.message}`);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0] // Store only the first file
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Helper to append form data
  const createFormData = (data) => {
    const form = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== '') {
        if (Array.isArray(data[key])) {
          data[key].forEach(val => form.append(`${key}[]`, val));
        } else {
          form.append(key, data[key]);
        }
      }
    });
    return form;
  }

  const handleBranchToggle = (branch) => {
    setFormData(prev => {
      const branches = prev.branch_names || [];
      const lowerBranch = String(branch).trim().toLowerCase();
      const index = branches.findIndex(b => String(b).trim().toLowerCase() === lowerBranch);
      if (index !== -1) {
        return { ...prev, branch_names: branches.filter((_, i) => i !== index) };
      } else {
        return { ...prev, branch_names: [...branches, branch] };
      }
    });
  };

  useEffect(() => {
    if (!isCompanyAdmin || selectedCompanies.length === 0) {
      setFormBranches(availableBranches);
      return;
    }
    const fetchFormBranches = async () => {
      try {
        const branchPromises = selectedCompanies.map(comp => axios.get(`${baseUrl || ''}/api/branches?company_name=${encodeURIComponent(comp)}`));
        const results = await Promise.all(branchPromises);
        let branches = [];
        results.forEach(res => {
          branches = [...branches, ...res.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.name))];
        });
        setFormBranches([...new Set(branches.filter(Boolean))]);
      } catch(err) { console.error(err); }
    };
    fetchFormBranches();
  }, [selectedCompanies, isCompanyAdmin, baseUrl, availableBranches]);

  const handleCompanyToggle = (company) => {
    setSelectedCompanies(prev =>
      prev.includes(company) ? prev.filter(c => c !== company) : [...prev, company]
    );
  };

  // Add new vehicle
  const handleAddVehicle = async (e) => {
    e.preventDefault();
    if (!canCreate) {
      setError("You do not have permission to add vehicles.");
      return;
    }
    const currentBaseUrl = baseUrl || '';

    try {
      setLoading(true);
      const activeContext = localStorage.getItem('active_company');
      if (activeContext === 'All' && selectedCompanies.length === 0) {
        setError("Please select at least one company.");
        setLoading(false);
        return;
      }

      const finalBranchNames = (formData.branch_names && formData.branch_names.length > 0)
        ? formData.branch_names
        : (user && user.branch_name ? [user.branch_name] : []);

      const companiesToProcess = activeContext === 'All' ? selectedCompanies : [activeContext || user.company_name];
      let successCount = 0;
      let errorMsgs = [];

      const payload = {
        ...formData,
        branch_names: finalBranchNames,
        company_names: companiesToProcess
      };

      const data = createFormData(payload);
      const headers = {
        'Content-Type': 'multipart/form-data'
      };

      try {
        await axios.post(`${currentBaseUrl}/api/vehicle/management`, data, { headers });
        successCount++;
      } catch (err) {
        errorMsgs.push(`Failed: ${err.response?.data?.error || err.message}`);
      }

      if (successCount > 0) {
        setMessage(`Successfully added ${successCount} vehicle entries! ${errorMsgs.length > 0 ? "Errors: " + errorMsgs.join(", ") : ""}`);
        
        if (location.state && location.state.returnTo && errorMsgs.length === 0) {
          navigate(location.state.returnTo, {
            state: {
              newVehicleNumber: formData.vehicle_number,
              preservedState: location.state.preservedState
            }
          });
          return;
        }

        
        resetForm();
        fetchVehicles(currentBaseUrl);
      } else {
        setError(`Failed to add: ${errorMsgs.join(", ")}`);
      }

      // Check for returnTo state
      if (location.state && location.state.returnTo) {
        navigate(location.state.returnTo, {
          state: {
            newVehicleNumber: formData.vehicle_number,
            preservedState: location.state.preservedState
          }
        });
        return; // Skip the rest as we are navigating away
      }

      
      resetForm();
      fetchVehicles(currentBaseUrl);
    } catch (err) {
      setError(`Failed to add vehicle: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Edit vehicle
  const handleEditVehicle = async (e) => {
    e.preventDefault();
    if (!canWrite) {
      setError("You do not have permission to edit vehicles.");
      return;
    }
    if (!selectedVehicle) return;
    const currentBaseUrl = baseUrl || '';

    try {
      setLoading(true);
      
      const payload = { ...formData };
      if (!payload.company_names || payload.company_names.length === 0) {
         payload.company_names = selectedVehicle.company_names || [selectedVehicle.company_name];
      }

      const data = createFormData(payload);
      const headers = {
        'Content-Type': 'multipart/form-data'
      };
      
      const endpoint = selectedVehicle.global_ref_id 
          ? `${currentBaseUrl}/api/vehicle/management/bulk/${selectedVehicle.global_ref_id}`
          : `${currentBaseUrl}/api/vehicle/management/${selectedVehicle._id}`;

      await axios.put(endpoint, data, { headers });
      setMessage('Vehicle updated successfully!');
      setSelectedVehicle(null);
      setSelectedVehicle(null);
      fetchVehicles(currentBaseUrl);
    } catch (err) {
      setError(`Failed to update vehicle: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Delete vehicle
  const handleDeleteVehicle = async () => {
    if (!canDelete) {
      setError("You do not have permission to delete vehicles.");
      return;
    }
    if (!selectedVehicle) return;
    const currentBaseUrl = baseUrl || '';

    try {
      setLoading(true);
      const headers = {};
      const endpoint = selectedVehicle.global_ref_id 
          ? `${currentBaseUrl}/api/vehicle/management/bulk/${selectedVehicle.global_ref_id}`
          : `${currentBaseUrl}/api/vehicle/management/${selectedVehicle._id}`;
      await axios.delete(endpoint, { headers });
      setMessage('Vehicle deleted successfully!');
      setShowDeleteConfirm(false);
      setSelectedVehicle(null);
      fetchVehicles(currentBaseUrl);
    } catch (err) {
      setError(`Failed to delete vehicle: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_number: '',
      vehicle_type: '',
      brand: '',
      model: '',
      fuel_type: '',
      is_company_owned: true,
      rc_number: '',
      rc_expiry: '',
      insurance_number: '',
      insurance_expiry: '',
      insurance_doc: null,
      pollution_expiry: '',
      pollution_doc: null,
      status: 'ACTIVE',
      branch_names: [],
    });
  }

  // Helper to resolve branches from any record
  const resolveBranches = (item) => {
    if (!item) return [];
    
    // Check for array fields first
    const arrayFields = ['branch_names', 'branch_name', 'branches', 'Branches', 'BranchNames', 'branch_ids'];
    for (const field of arrayFields) {
      if (Array.isArray(item[field]) && item[field].length > 0) {
        return item[field].map(s => String(s).trim());
      }
    }

    // Check for comma-separated string fields
    const stringFields = ['branch_names', 'branch_name', 'branch', 'branchName', 'BranchNames', 'branches', 'branchNameList'];
    for (const field of stringFields) {
      const val = item[field];
      if (typeof val === 'string' && val.trim() !== '') {
        return val.split(',').map(s => s.trim()).filter(Boolean);
      }
    }

    return [];
  };

  // Open add modal
  const openAddModal = () => {
    resetForm();
    setSelectedVehicle(null);
    setError(null);
    setMessage('');
  };

  // Open edit modal
  const openEditModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    if (vehicle.company_names && vehicle.company_names.length > 0) {
      setSelectedCompanies(vehicle.company_names);
    } else if (vehicle.company_name) {
      setSelectedCompanies([vehicle.company_name]);
    } else {
      setSelectedCompanies([]);
    }
    setFormData({
      vehicle_number: vehicle.vehicle_number || '',
      vehicle_type: vehicle.vehicle_type || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      fuel_type: vehicle.fuel_type || '',
      is_company_owned: vehicle.is_company_owned,
      rc_number: vehicle.rc_number || '',
      rc_expiry: vehicle.rc_expiry ? vehicle.rc_expiry.split('T')[0] : '',
      insurance_number: vehicle.insurance_number || '',
      insurance_expiry: vehicle.insurance_expiry ? vehicle.insurance_expiry.split('T')[0] : '',
      insurance_doc: vehicle.insurance_doc || null,
      pollution_expiry: vehicle.pollution_expiry ? vehicle.pollution_expiry.split('T')[0] : '',
      pollution_doc: vehicle.pollution_doc || null,
      status: vehicle.status || 'ACTIVE',
      branch_names: resolveBranches(vehicle),
    });
    setShowEditModal(true);
    setError(null);
    setMessage('');
  };

  // Open delete confirmation
  const openDeleteConfirm = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDeleteConfirm(true);
  };

  // Close modals
  const closeModal = (modal) => {
    if (modal === 'add') 
    if (modal === 'edit') {
      setSelectedVehicle(null);
      setSelectedVehicle(null);
    }
    if (modal === 'delete') setShowDeleteConfirm(false);
    setError(null);
    setMessage('');
  };

  // Filtered vehicles based on search and filters
  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = (vehicle.vehicle_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (vehicle.brand || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (vehicle.model || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesCompany = true;
    if (selectedViewCompany) {
      matchesCompany = vehicle.company_names ? vehicle.company_names.includes(selectedViewCompany) : vehicle.company_name === selectedViewCompany;
    }

    let matchesBranch = true;
    if (selectedBranchFilter) {
      const vBranches = vehicle.branch_names || [];
      matchesBranch = vBranches.includes(selectedBranchFilter) || vehicle.branch_name === selectedBranchFilter;
    }

    return matchesSearch && matchesCompany && matchesBranch;
  });

  // Pagination
  const indexOfLastVehicle = currentPage * vehiclesPerPage;
  const indexOfFirstVehicle = indexOfLastVehicle - vehiclesPerPage;
  const currentVehicles = filteredVehicles.slice(indexOfFirstVehicle, indexOfLastVehicle);
  const totalPages = Math.ceil(filteredVehicles.length / vehiclesPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Status badge style
  const getStatusBadge = (status) => {
    const colors = {
      ACTIVE: { bg: '#d4edda', color: '#155724' },
      INACTIVE: { bg: '#f8d7da', color: '#721c24' },
      IN_MAINTENANCE: { bg: '#fff3cd', color: '#856404' },
    };
    return colors[status] || colors.ACTIVE;
  };

  const getOwnedBadge = (isCompanyOwned) => {
    return isCompanyOwned ? <><FaBuilding /> Company</> : <><FaUser /> Employee</>;
  };

  // Helper for rendering file link (Text) - Used in Edit Modal fallback
  const renderFileLink = (path, label) => {
    if (!path) return <span style={{ color: '#95a5a6', fontSize: '0.8rem' }}>No file</span>;
    // Use baseUrl || '' here too
    const currentBaseUrl = baseUrl || '';
    return (
      <a href={`${currentBaseUrl}/api/images/${path}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#3498db', textDecoration: 'none' }}>
        <FaEye /> View {label}
      </a>
    );
  };

  // NEW: Helper for rendering file IMAGE PREVIEW - Used in Table
  const renderFilePreview = (path, label) => {
    if (!path) return <span style={{ color: '#95a5a6', fontSize: '0.8rem' }}>No file</span>;

    const isImage = (fileName) => /\.(jpg|jpeg|png|gif|webp|jfif|ico|svg)$/i.test(fileName);
    const isPdf = (fileName) => /\.pdf$/i.test(fileName);
    const currentBaseUrl = baseUrl || '';

    const previewStyle = {
      width: '50px',
      height: '50px',
      borderRadius: '8px',
      overflow: 'hidden',
      border: '1px solid #dfe6e9',
      cursor: 'pointer',
      backgroundColor: '#f1f2f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      color: '#7f8c8d'
    };

    if (isImage(path)) {
      return (
        <div
          style={previewStyle}
          onClick={() => window.open(`${currentBaseUrl}/api/images/${path}`, '_blank')}
          title={`View ${label}`}
        >
          <img
            src={`${currentBaseUrl}/api/images/${path}`}
            alt={label}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              e.target.style.display = 'none';
              // Fallback to generic icon if image fails
              e.target.parentNode.children[1].style.display = 'block';
            }}
          />
          <FaFileAlt style={{ display: 'none' }} />
        </div>
      );
    } else {
      return (
        <div
          style={previewStyle}
          onClick={() => window.open(`${currentBaseUrl}/api/images/${path}`, '_blank')}
          title={`View ${label}`}
        >
          {isPdf(path) ? <FaFileAlt style={{ color: '#e74c3c' }} /> : <FaFileAlt style={{ color: '#3498db' }} />}
        </div>
      );
    }
  };

  // Common Form Content - UPDATED: No required attributes
  const renderFormContent = () => {
    const currentBaseUrl = baseUrl || '';
    return (
      <>
        
            {/* --- Company and Branch Assignment Section --- */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Vehicle Number <span style={{ color: '#e74c3c' }}>*</span></label>
            <input type="text"
              name="vehicle_number"
              placeholder="Vehicle Number"
              value={formData.vehicle_number}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Vehicle Type <span style={{ color: '#e74c3c' }}>*</span></label>
            <select
              name="vehicle_type"
              value={formData.vehicle_type}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            >
              
          <option value="">Select Vehicle Type</option>
          <option value="BIKE">BIKE</option>
          <option value="SCOOTER">SCOOTER</option>
          <option value="CAR">CAR</option>
          <option value="VAN">VAN</option>
        
            </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Brand <span style={{ color: '#e74c3c' }}>*</span></label>
            <input type="text"
              name="brand"
              placeholder="Brand"
              value={formData.brand}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Model <span style={{ color: '#e74c3c' }}>*</span></label>
            <input type="text"
              name="model"
              placeholder="Model"
              value={formData.model}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Fuel Type <span style={{ color: '#e74c3c' }}>*</span></label>
            <select
              name="fuel_type"
              value={formData.fuel_type}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            >
              
          <option value="">Select Fuel Type</option>
          <option value="PETROL">PETROL</option>
          <option value="DIESEL">DIESEL</option>
          <option value="ELECTRIC">ELECTRIC</option>
        
            </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', padding: '10px 0' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '15px', fontWeight: '700', color: '#0f172a', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_company_owned"
                checked={formData.is_company_owned}
                onChange={handleInputChange}
                style={{ width: '20px', height: '20px', accentColor: '#604BE8', cursor: 'pointer' }}
              />
              Is Company Owned Vehicle?
            </label>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>RC Number <span style={{ color: '#e74c3c' }}>*</span></label>
            <input type="text"
              name="rc_number"
              placeholder="RC Number"
              value={formData.rc_number}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>RC Expiry Date <span style={{ color: '#e74c3c' }}>*</span></label>
            <input
              type="date"
              name="rc_expiry"
              value={formData.rc_expiry}
              onChange={handleInputChange}
              onClick={(e) => { try { e.target.showPicker(); } catch (err) { } }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Insurance Number <span style={{ color: '#e74c3c' }}>*</span></label>
            <input type="text"
              name="insurance_number"
              placeholder="Insurance Number"
              value={formData.insurance_number}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Insurance Expiry Date <span style={{ color: '#e74c3c' }}>*</span></label>
            <input
              type="date"
              name="insurance_expiry"
              value={formData.insurance_expiry}
              onChange={handleInputChange}
              onClick={(e) => { try { e.target.showPicker(); } catch (err) { } }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            />
        </div>
        <div style={{ marginBottom: '15px', padding: '10px', borderRadius: '5px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px', fontSize: '0.9rem', color: '#555' }}>
            <FaFileUpload /> Upload Insurance Document
          </label>
          {formData.insurance_doc && typeof formData.insurance_doc === 'string' ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px',
              background: '#f1f2f6',
              borderRadius: '5px',
              border: '1px solid #dfe6e9'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                <FaFileAlt style={{ color: '#3498db' }} />
                <a
                  href={`${currentBaseUrl}/api/images/${formData.insurance_doc}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: '#2980b9', fontSize: '0.9rem', whiteWhiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}
                >
                  View Existing Document
                </a>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, insurance_doc: null, delete_insurance_doc: true })}
                style={{
                  border: 'none',
                  background: '#e74c3c',
                  color: 'white',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <FaTrash /> Delete
              </button>
            </div>
          ) : (
            <>
              <input
                type="file"
                name="insurance_doc"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.svg"
                onChange={handleInputChange}
                style={{ width: '100%' }}
              />
            {formData.insurance_doc && typeof formData.insurance_doc === 'object' && (
                <div style={{ fontSize: '0.8rem', color: '#27ae60', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <FaCheckCircle /> Selected: {formData.insurance_doc.name}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Pollution (PUC) Expiry Date <span style={{ color: '#e74c3c' }}>*</span></label>
            <input
              type="date"
              name="pollution_expiry"
              value={formData.pollution_expiry}
              onChange={handleInputChange}
              onClick={(e) => { try { e.target.showPicker(); } catch (err) { } }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            />
        </div>
        <div style={{ marginBottom: '15px', padding: '10px', borderRadius: '5px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px', fontSize: '0.9rem', color: '#555' }}>
            <FaFileUpload /> Upload Pollution Document
          </label>
          {formData.pollution_doc && typeof formData.pollution_doc === 'string' ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px',
              background: '#f1f2f6',
              borderRadius: '5px',
              border: '1px solid #dfe6e9'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                <FaFileAlt style={{ color: '#3498db' }} />
                <a
                  href={`${currentBaseUrl}/api/images/${formData.pollution_doc}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: '#2980b9', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}
                >
                  View Existing Document
                </a>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, pollution_doc: null, delete_pollution_doc: true })}
                style={{
                  border: 'none',
                  background: '#e74c3c',
                  color: 'white',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <FaTrash /> Delete
              </button>
            </div>
          ) : (
            <>
              <input
                type="file"
                name="pollution_doc"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.svg"
                onChange={handleInputChange}
                style={{ width: '100%' }}
              />
            {formData.pollution_doc && typeof formData.pollution_doc === 'object' && (
                <div style={{ fontSize: '0.8rem', color: '#27ae60', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <FaCheckCircle /> Selected: {formData.pollution_doc.name}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Status <span style={{ color: '#e74c3c' }}>*</span></label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', outline: 'none', color: '#0f172a' }}
            >
              
          <option value="ACTIVE">ACTIVE</option>
          <option value="IN_MAINTENANCE">IN_MAINTENANCE</option>
          <option value="INACTIVE">INACTIVE</option>
        
            </select>
        </div>
      </>
    );
  };

  if (loading && vehicles.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f8f9fa' // Clean background for loading
      }}>
        <div style={{
          textAlign: 'center',
          color: '#3498db',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '15px'
        }}>
          {/* Custom Spinner */}
          <div className="spinner" style={{
            width: '50px',
            height: '50px',
            border: '5px solid #e9ecef',
            borderTop: '5px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '1.2rem', color: '#7f8c8d' }}>Loading vehicles...</p>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const userStr = localStorage.getItem('user');
  const userObj = userStr ? JSON.parse(userStr) : {};
  const isAdminRole = checkIsAdmin(userObj);


  if (!canRead && !isAdminRole && !loading && !configLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)'
      }}>
        <div style={{ textAlign: 'center', color: '#e74c3c', fontSize: '18px', padding: '30px', background: '#fff', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <FaExclamationTriangle size={50} style={{ marginBottom: '15px' }} />
          <h2 style={{ marginBottom: '10px' }}>Access Denied</h2>
          <p>You do not have permission to view Vehicle Management.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/admin')}
            style={{ marginTop: '20px', padding: '10px 20px', borderRadius: '50px', background: '#3498db', color: '#fff', border: 'none', cursor: 'pointer' }}
          >
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
        <style>
          {`
            .premium-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
            }
            .premium-table th {
              background: #06D6A0;
              color: #ffffff;
              font-weight: 600;
              font-size: 15px;
              text-transform: capitalize;
              letter-spacing: 0.3px;
              padding: 16px 24px;
              text-align: left;
              border: none;
              position: sticky;
              top: 0;
              z-index: 10;
            }
            .premium-table th:first-child { border-top-left-radius: 10px; }
            .premium-table th:last-child { border-top-right-radius: 10px; }
            .premium-table td {
              padding: 16px 24px;
              color: #4A4A5A;
              font-size: 14px;
              font-weight: 500;
              border-bottom: 1px solid #F1F3F8;
              transition: all 0.2s ease;
              vertical-align: middle;
            }
            .premium-table tr:nth-child(odd) td { background: #ffffff; }
            .premium-table tr:nth-child(even) td { background: #FAFBFD; }
            .premium-table tr:hover td { background: #F1F4F9; }
            .premium-table tr:last-child td { border-bottom: none; }
            
            .action-btn {
              width: 42px;
              height: 42px;
              border-radius: 10px;
              display: flex;
              justify-content: center;
              align-items: center;
              cursor: pointer;
              transition: all 0.2s ease;
              border: none;
            }
            .action-btn-edit {
              background: rgba(255, 159, 4, 0.15);
              color: #FF9F04;
            }
            .action-btn-edit:hover {
              background: #FF9F04 !important;
              color: #ffffff !important;
              box-shadow: 0 4px 12px rgba(255, 159, 4, 0.3);
            }
            .action-btn-delete {
              background: rgba(255, 89, 94, 0.15);
              color: #FF595E;
            }
            .action-btn-delete:hover {
              background: #FF595E !important;
              color: #ffffff !important;
              box-shadow: 0 4px 12px rgba(255, 89, 94, 0.3);
            }
          `}
        </style>
 <div style={{ minHeight: '100vh', background: '#F8F9FD', fontFamily: "'Gilroy', 'Outfit', 'Inter', sans-serif", color: '#1B1B29', display: 'flex', flexDirection: 'column' }}>
        {/* Top Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', padding: '36px 48px 16px 48px', width: '100%', boxSizing: 'border-box', flex: '0 0 auto' }}>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                <button
                  onClick={() => {
                    if (location.state && location.state.returnTo) {
                      navigate(location.state.returnTo, { state: { preservedState: location.state.preservedState } });
                    } else {
                      navigate('/admin');
                    }
                  }}
                  style={{
                    height: '44px', padding: '0 20px', borderRadius: '10px', border: '1px solid #E2E6EF',
                    background: '#ffffff', color: '#604BE8', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
                  }}
                  disabled={loading}
                >
                  <FaArrowLeft /> Back to Admin
                </button>
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#1B1B29', letterSpacing: '-0.5px' }}>
                  <FaCar style={{ color: '#604BE8', fontSize: '2rem', verticalAlign: 'middle', marginRight: '10px' }} /> 
                  Vehicle Management
                </h1>
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              {canCreate && (
                    <button type="submit" form="vehicleForm" style={{ padding: '12px 24px', background: '#604BE8', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', boxShadow: '0 4px 8px rgba(96, 75, 232, 0.25)', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      {loading ? 'Processing...' : (selectedVehicle ? 'Update Vehicle' : 'Save Vehicle')}
                    </button>
                )}
              {selectedVehicle && (
                    <button type="button" onClick={() => { setSelectedVehicle(null); resetForm(); }} style={{ padding: '12px 24px', background: '#94a3b8', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', boxShadow: '0 2px 4px rgba(148, 163, 184, 0.3)', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Cancel Edit
                    </button>
                )}
            </div>
        </div>

        <div style={{ width: '100%', padding: '20px 48px 48px', boxSizing: 'border-box' }}>
          <div style={{ backgroundColor: '#ffffff', padding: '40px', borderRadius: '20px', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)', border: '1px solid #E2E6EF', overflow: 'hidden' }}>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
              {/* Inline Form */}
              {canCreate && (
                    <div style={{ background: '#F8F9FA', padding: '25px', borderRadius: '12px', border: '1px solid #E2E6EF' }}>
                        <form id="vehicleForm" onSubmit={handleFormSubmitClick} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #E2E6EF', paddingBottom: '15px' }}>
                              <h3 style={{ margin: 0, color: '#1B1B29', fontSize: '1.4rem' }}>{selectedVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</h3>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
                            {renderFormContent()}
                          </div>
                        </form>
                    </div>
                )}
                
              {/* Vehicles Table & Filter Bar */}
                <div style={{ width: '100%' }}>
                {/* Filter Bar */}
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap', background: '#F8F9FA', padding: '20px', borderRadius: '12px', border: '1px solid #E2E6EF' }}>
                    <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
                      <FaSearch style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                      <input type="text" placeholder="Search Vehicles..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ width: '100%', padding: '12px 12px 12px 45px', borderRadius: '8px', border: '1px solid #E2E6EF', outline: 'none' }} />
                    </div>

                  {companyOptions.length > 1 && (
                      <div style={{ width: '200px' }}>
                        <select value={selectedViewCompany} onChange={(e) => setSelectedViewCompany(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #E2E6EF', outline: 'none', backgroundColor: '#fff', fontWeight: '500' }}>
                          <option value="">All Companies</option>
                        {companyOptions.map(comp => <option key={comp} value={comp}>{comp}</option>)}
                        </select>
                      </div>
                    )}

                  {((companyOptions.length > 1 && selectedViewCompany) || companyOptions.length <= 1) && !userBranch && (
                      <div style={{ width: '200px' }}>
                        <select value={selectedBranchFilter} onChange={(e) => setSelectedBranchFilter(e.target.value)} style={{ padding: '12px 20px', width: '100%', borderRadius: '8px', border: '1px solid #E2E6EF', outline: 'none', background: 'white', fontWeight: '600' }}>
                          <option value="">All Branches</option>
                        {(filterBranches.length > 0 ? filterBranches : availableBranches).map(br => <option key={br} value={br}>{br}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1B1B29', margin: 0 }}>Vehicle Profiles ({filteredVehicles.length})</h2>
                  </div>

                {/* Vehicles Table Container */}
                  <div style={{ width: '100%', overflowX: 'auto', background: '#ffffff', borderRadius: '12px', border: '1px solid #E2E6EF', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)' }}>
                  {filteredVehicles.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#777777' }}>No vehicles found.</div>
                    ) : (
                        <table className="premium-table">
                          <thead>
                            <tr>
                              <th>Vehicle</th>
                              <th>Type/Fuel</th>
                              <th>Ownership</th>
                              <th>Status</th>
                            {isCompanyAdmin && <th>Assignment</th>}
                              <th>Documents</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                          {filteredVehicles.map((vehicle, index) => (
                                <tr key={vehicle._id || index}>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#f0eeff', color: '#604BE8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                                        <FaCar />
                                      </div>
                                      <div>
                                        <div style={{ fontWeight: '600', color: '#1B1B29', fontSize: '14px' }}>{vehicle.vehicle_number}</div>
                                        <div style={{ color: '#777777', fontSize: '12px' }}>{vehicle.brand} {vehicle.model}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div>{vehicle.vehicle_type || 'N/A'}</div>
                                    <div style={{ fontSize: '12px', color: '#777' }}>{vehicle.fuel_type || 'N/A'}</div>
                                  </td>
                                  <td>
                                    <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: vehicle.is_company_owned ? '#e8f5e9' : '#e3f2fd', color: vehicle.is_company_owned ? '#2e7d32' : '#1976d2' }}>
                                    {vehicle.is_company_owned ? 'Company Owned' : 'Personal'}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: vehicle.status === 'ACTIVE' ? '#e8f5e9' : vehicle.status === 'IN_MAINTENANCE' ? '#fff3e0' : '#fee2e2', color: vehicle.status === 'ACTIVE' ? '#2e7d32' : vehicle.status === 'IN_MAINTENANCE' ? '#e65100' : '#c0392b' }}>
                                    {vehicle.status}
                                    </span>
                                  </td>
                                {isCompanyAdmin && (
                                    <td>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                      {(vehicle.company_names || [vehicle.company_name]).filter(Boolean).map((comp, idx) => (
                                          <span key={idx} style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', color: '#475569', display: 'inline-block', marginBottom: '2px' }}>🏢 {comp}</span>
                                        ))}
                                      {vehicle.branch_names && vehicle.branch_names.length > 0 && (
                                          vehicle.branch_names.map((br, idx) => (
                                            <span key={`br-${idx}`} style={{ background: '#ecfdf5', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', color: '#059669', display: 'inline-block', marginBottom: '2px' }}>📍 {br}</span>
                                          ))
                                        )}
                                      </div>
                                    </td>
                                  )}
                                  <td>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                    {renderFilePreview(vehicle.insurance_doc, 'Insurance')}
                                    {renderFilePreview(vehicle.pollution_doc, 'Pollution (PUC)')}
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                    {canWrite && <button onClick={() => openEditModal(vehicle)} className="action-btn action-btn-edit" title="Edit Vehicle"><FaEdit style={{ fontSize: '20px' }} /></button>}
                                    {canDelete && <button onClick={() => { setSelectedVehicle(vehicle); setShowDeleteConfirm(true); }} className="action-btn action-btn-delete" title="Delete Vehicle"><FaTrash style={{ fontSize: '20px' }} /></button>}
                                    </div>
                                  </td>
                                </tr>
                            ))}
                          </tbody>
                        </table>
                    )}
                  </div>
                </div>
            </div>
        </div>

      {/* Modals */}
      {showSaveConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10002, backdropFilter: 'blur(3px)' }}>
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '16px', maxWidth: '700px', width: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', animation: 'fadeInScale 0.3s ease-out' }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#0f172a', textAlign: 'center', fontSize: '1.5rem' }}>Assign Companies & Branches</h3>
                
              {isCompanyAdmin && (
                <div style={{ marginTop: '20px', background: 'white', padding: '15px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#1e293b', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaBuilding style={{ color: '#3b82f6' }} /> Assign to Companies *
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  {(companyOptions.length > 0 ? companyOptions : allCompanies).map((comp, idx) => {
                      const isSelected = selectedCompanies.includes(comp);
                      return (
                        <div key={idx} style={{ border: isSelected ? '1.5px solid #3b82f6' : '1.5px solid #e2e8f0', padding: '10px', borderRadius: '8px', backgroundColor: isSelected ? '#eff6ff' : '#ffffff', cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => {
                          if (isSelected) setSelectedCompanies(prev => prev.filter(c => c !== comp));
                          else setSelectedCompanies(prev => [...prev, comp]);
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="checkbox" checked={isSelected} readOnly style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                            <span style={{ fontWeight: '500', color: '#1e293b', fontSize: '0.9rem' }}>{comp}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {isCompanyAdmin && selectedCompanies.length > 0 && formBranches.length > 0 && (
                <div style={{ marginTop: '10px', background: 'white', padding: '15px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#1e293b', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaBuilding style={{ color: '#10b981' }} /> Assign to Branches
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  {formBranches.map((brName, idx) => {
                      const isSelected = (formData.branch_names || []).includes(brName);
                      return (
                        <div key={idx} style={{ border: isSelected ? '1.5px solid #10b981' : '1.5px solid #e2e8f0', padding: '10px', borderRadius: '8px', backgroundColor: isSelected ? '#ecfdf5' : '#ffffff', cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => handleBranchToggle(brName)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="checkbox" checked={isSelected} readOnly style={{ width: '16px', height: '16px', accentColor: '#10b981', cursor: 'pointer' }} />
                            <span style={{ fontWeight: '500', color: '#1e293b', fontSize: '0.9rem' }}>{brName}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

        

                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '25px' }}>
                    <button onClick={confirmAndSave} style={{ flex: 1, padding: '14px 24px', background: '#604BE8', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', transition: 'all 0.2s' }}>Confirm and Save</button>
                    <button onClick={() => setShowSaveConfirm(false)} style={{ flex: 1, padding: '14px 24px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', transition: 'all 0.2s' }}>Cancel</button>
                </div>
            </div>
        </div>
      )}

      {message && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10002, backdropFilter: 'blur(3px)' }}>
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '16px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', animation: 'fadeInScale 0.3s ease-out' }}>
                <div style={{ width: '60px', height: '60px', background: '#dcfce7', color: '#16a34a', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '30px', margin: '0 auto 15px' }}>
                    <FaCheckCircle />
                </div>
                <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Success</h3>
                <p style={{ color: '#64748b', margin: '0 0 20px 0' }}>{message}</p>
                <button onClick={() => setMessage('')} style={{ padding: '10px 20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Awesome!</button>
            </div>
        </div>
      )}
      
      {error && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10002, backdropFilter: 'blur(3px)' }}>
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '16px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', animation: 'fadeInScale 0.3s ease-out' }}>
                <div style={{ width: '60px', height: '60px', background: '#fee2e2', color: '#e74c3c', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '30px', margin: '0 auto 15px' }}>
                    <FaExclamationTriangle />
                </div>
                <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Error</h3>
                <p style={{ color: '#64748b', margin: '0 0 20px 0' }}>{error}</p>
                <button onClick={() => setError(null)} style={{ padding: '10px 20px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Close</button>
            </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10002, backdropFilter: 'blur(3px)' }} onClick={(e) => { if (e.target === e.currentTarget) closeModal('delete'); }}>
          <div style={{ backgroundColor: '#ffffff', padding: '30px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', textAlign: 'center', animation: 'fadeInScale 0.3s ease-out' }}>
            <div style={{ width: '60px', height: '60px', background: '#fee2e2', color: '#e74c3c', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '24px', margin: '0 auto 15px' }}>
                <FaTrash />
            </div>
            <h3 style={{ color: '#1e293b', margin: '0 0 10px 0', fontSize: '1.5rem' }}>Confirm Delete</h3>
            <p style={{ color: '#64748b', marginBottom: '25px', fontSize: '1rem', lineHeight: '1.5' }}>Are you sure you want to delete {selectedVehicle?.vehicle_number}? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button onClick={handleDeleteVehicle} style={{ flex: 1, padding: '12px 24px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: '600' }}>{loading ? 'Deleting...' : 'Yes, Delete'}</button>
              <button onClick={() => closeModal('delete')} style={{ flex: 1, padding: '12px 24px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: '600' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
    </>
  );
}

export default VehicleManagement;
